import {Observable, ReplaySubject, EMPTY, forkJoin, of} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import {AppConfig} from 'app/app.config';
import {APIService} from 'app/services/api.service';
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";

import {LoaderService} from './loader.service';

export type authUserType = {
  apiVersion: string;
  displayName: null | string;
  firstName: string;
  isAccountant: boolean;
  isClient: boolean;
  last_login: string;
  reset_token: null | string;
  roles: string[];
  token_type: string;
  trustedComputer: null | string;
  userName: string;
  uId: string;
  nameId: number;
  reRgs?: reRgType[];
  reRgId?: number;
};

export type profileType = {
  EmailAddress: string;
  Fax: string;
  Name: string;
  NameId: number;
  PhoneNumber: string;
  TwoFAEmail: string;
  TwoFAPhone: string;
  UserId: string;
  UserName: string;
  AdvisorId?: number;
  AdvisorType?: string;
  ClientIds?: string;
  Address1: string;
  Address2: string;
  Address3: string;
  City: string;
  Province: string;
  Country: string;
  Postal: string;
};

export type reRgType = {
  ReRgId: number;
  ClientName: string;
  RergType: string; // Main types are: Client, Prospect, PGF or Unlinked if there is no ReRg linked to the login
  Access: string;
  IsDefault: boolean;
  IsPershing: boolean;
  UsersWithAccess: string;
};

@Injectable()
export class AuthenticationService {
  private api = inject(APIService);
  private config = inject(AppConfig);
  private modalService = inject(NgbModal);
  private loaderService = inject(LoaderService);


  public user?: authUserType;
  public reRg = new ReplaySubject<undefined | reRgType>(1);
  public currentReRg?: reRgType;
  public newNotificationsCount = new ReplaySubject <number>(1);

  public isLoggedIn = false;

  private profile?: profileType;
  private accessListOverUser: Observable<reRgType[]> | undefined;

  private vd_current = '';
  private vd_all = new ReplaySubject <null | string[]>(1);
  private latest_vd = '';
  private access_list: string[] = [];

  constructor() {
    this.setReRg(undefined);
  }

  logUserIn(user: authUserType) {
    sessionStorage.removeItem('ReRgId');
    this.api.clearDocumentsCache();
    if (user) {
      this.setUser(user);
      this.isLoggedIn = true;
      const rerg = this.user?.reRgs?.find(f=> this.user?.reRgId ? this.user?.reRgId === f.ReRgId : f.IsDefault);
      return rerg ? this.setReRg(rerg) : this.setUnlinkedReRg();
    }
    this.logUserOut();
    return EMPTY;
  }

  setUser(user: authUserType) {
    this.user = {
      ...user,
      isClient: !user.roles || user.roles.length == 0 || user.roles.indexOf('Client') > -1,
      isAccountant: user.roles && user.roles.indexOf('Accountant') > -1
    };
  }

  setUnlinkedReRg() {
    return this.setReRg({
      ReRgId: this.user!.nameId,
      ClientName: this.user?.displayName || '',
      RergType: 'Unlinked',
      Access: 'Update_Personal_Information',
      UsersWithAccess:'',
      IsDefault: true,
      IsPershing: false
    });
  }

  setReRg(reRg: reRgType | undefined) {
    if (!reRg) {
      this.currentReRg = reRg;
      sessionStorage.removeItem('valuationDate');
      sessionStorage.removeItem('ReRgId');
      this.vd_all.next(null);
      this.vd_current = '';
      this.reRg.next(reRg);
      this.access_list = [];
      return EMPTY;
    }
    this.access_list = reRg.Access ? reRg.Access.split(',') : [];
    this.loaderService.display(true, 'reRgProfile');
    sessionStorage.setItem('ReRgId', reRg.ReRgId.toString());

    return forkJoin([
      this.api.get<profileType>(this.config.profile.profileUrl+'/'+reRg.ReRgId),
      this.api.get<string[]>(this.config.portfolio.portfolioValuationDatesUrl+'/'+reRg.ReRgId)
      ]).pipe(tap({
        next: ([profileData, valuationDates]) => {
          this.currentReRg = reRg;
          this.profile = profileData;

          const vd = sessionStorage.getItem('valuationDate'); //restore valuationDate if user reloads the page
          this.latest_vd = valuationDates[0];
          this.vd_all.next(valuationDates);
          this.vd_current = (vd && vd != valuationDates[0] && valuationDates.indexOf(vd)>-1) ? vd : valuationDates[0];

          this.loaderService.display(false, 'reRgProfile');
          this.reRg.next(reRg);
          if (this.user?.isClient) this.loadNewNotificationsCount();
        },
        error: () => {
          this.loaderService.display(false, 'reRgProfile');
          alert('Something went wrong. Please try again later.');
        }
      }
    ));
  }

  getHomeLink() {
    if (this.currentReRg?.RergType === 'PGF') return '/pgf';
    else if (!this.user?.isClient) return '/document';
    return '/welcome';
  }

  logUserOut() {
    this.modalService.dismissAll();
    if (!this.user) return;
    this.api.get(this.config.login.logoutUrl).pipe(catchError(() => of([]))).subscribe();
    this.user = undefined;
    this.setReRg(undefined);
    this.isLoggedIn = false;
    delete this.profile;
    delete this.accessListOverUser;
  }

  updateToken(token: authUserType) {
    this.setUser(token);
  }

  getProfile() {
    return this.profile;
  }

  public loadNewNotificationsCount() {
    this.api.get<number>(`${this.config.profile.notificationCountUrl}/${this.currentReRg?.ReRgId}`).subscribe(response =>{
      this.newNotificationsCount.next(response);
    });
  }

  public getNewNotificationsCount() {
    return this.newNotificationsCount.asObservable();
  }

  public getUser() {
    return this.user;
  }

  public getReRg() {
    return this.reRg;
  }

  public getCurrentReRg() {
    return this.currentReRg;
  }

  public hasAccess(access:string) {
    return this.access_list.includes(access);
  }

  getAllValuationDates() {
    return this.vd_all.asObservable();
  }

  setCurrentValuationDate(data: string): void {
    if (this.latest_vd == data) {
      sessionStorage.removeItem('valuationDate');
    } else {
      sessionStorage.setItem('valuationDate', data);
    }
    this.vd_current = data;
  }

  getCurrentValuationDate() {
    return this.vd_current;
  }

  public openKYC(redirect = false, reRgId?:number) {
    const isImpersonated = !!sessionStorage.getItem('guid');
    if (!this.user) return;
    this.api.get<string>(this.config.profile.getKycGuidUrl + '/' + (reRgId ?? this.currentReRg?.ReRgId) + '/' + isImpersonated).subscribe(res => {
      if (res) {
        if (redirect) {
          window.location.href = this.config.KYCUrl+res+"&isredirect=1";
        } else {
          const a = document.createElement("a");
          document.body.appendChild(a);
          a.href = this.config.KYCUrl+res;
          a.target = "_blank";
          a.click();
          a.remove();
        }
      }
    });
  }
}
